import { z } from 'zod';

import type {
  HandbookPersonalization,
  HandbookProvinceCode
} from '@paction-2.0/util/constants/breedHandbooks';

import { ApiError } from '../client';
import {
  getProviderJson,
  postProviderJson,
  putProviderJson
} from '../providerClient';

const titledTextSchema = z.object({ title: z.string(), text: z.string() });

const topicSchema = z.object({
  key: z.string(),
  kind: z.enum(['exam', 'dna', 'record']),
  title: z.string(),
  whatItChecks: z.string(),
  whyItMatters: z.string(),
  howItIsDone: z.string(),
  timing: z.string(),
  results: z.array(z.object({ label: z.string(), meaning: z.string() })),
  questions: z.array(z.string()),
  goodAnswer: z.string()
});

const screeningSchema = z.object({
  screening: z.string(),
  considerations: z.string().nullable(),
  tooltip: z.string().nullable(),
  isOptional: z.boolean(),
  topic: topicSchema.nullable()
});

const breedSummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
  photoUrl: z.string().nullable(),
  gallery: z.array(z.string()),
  lifeSpan: z.string().nullable()
});

const chapterSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  personalized: z.boolean(),
  bonus: z.boolean().optional()
});

const breedRefSchema = z.object({ slug: z.string(), name: z.string() });

const comparisonBreedSchema = breedRefSchema.extend({
  photoUrl: z.string().nullable().default(null)
});

const comparisonRowSchema = z.object({
  label: z.string(),
  values: z.tuple([z.string(), z.string()])
});

const bonusSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  summary: z.string(),
  shared: z.array(z.object({ label: z.string(), items: z.array(z.string()) })),
  columns: z.tuple([z.array(z.string()), z.array(z.string())]).nullable(),
  rows: z.array(comparisonRowSchema),
  links: z.array(z.object({ label: z.string(), path: z.string() }))
});

const bonusComparisonSchema = z.object({
  heading: z.string(),
  intro: z.string(),
  breeds: z.tuple([comparisonBreedSchema, comparisonBreedSchema]),
  glance: z.array(comparisonRowSchema),
  sections: z.array(bonusSectionSchema),
  decisions: z.array(z.string()),
  closing: z.string()
});

const conceptSchema = z.object({
  key: z.enum(['screening', 'genetic', 'puppyCare']),
  title: z.string(),
  whatItIs: z.string(),
  whoDoesIt: z.string(),
  whatItTellsYou: z.string()
});

const introductionSchema = z.object({
  heading: z.string(),
  lead: z.string(),
  concepts: z.array(conceptSchema),
  closing: z.string()
});

const glanceSchema = z.array(
  z.object({ label: z.string(), value: z.string() })
);

const handbookSchema = z.object({
  breed: breedSummarySchema,
  preparedFor: z.string().nullable(),
  personalized: z.boolean(),
  chapters: z.array(chapterSchema),
  introduction: introductionSchema,
  whyItMatters: z.object({
    heading: z.string(),
    paragraphs: z.array(z.string()),
    points: z.array(titledTextSchema)
  }),
  about: z.object({
    heading: z.string(),
    summary: z.string().nullable(),
    atAGlance: glanceSchema,
    temperament: z.array(z.string()),
    personality: z.array(titledTextSchema),
    care: z.array(titledTextSchema),
    challenges: z
      .object({ intro: z.string(), items: z.array(titledTextSchema) })
      .default({ intro: '', items: [] })
  }),
  enrichment: z.object({
    heading: z.string(),
    overview: z.string(),
    intro: z.string(),
    fromBreedProfile: z.string().nullable(),
    bredFor: z
      .object({
        heading: z.string(),
        jobs: z.array(z.string()),
        summary: z.string(),
        activities: z.array(titledTextSchema),
        note: z.string()
      })
      .nullable()
      .default(null),
    ideas: z.array(titledTextSchema),
    forYourHome: z.array(titledTextSchema).default([])
  }),
  match: z
    .object({
      heading: z.string(),
      intro: z.string(),
      factors: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          fit: z.enum(['good', 'plan', 'consider']),
          headline: z.string(),
          detail: z.string()
        })
      ),
      summary: z.string()
    })
    .nullable(),
  life: z.object({
    heading: z.string(),
    intro: z.string(),
    alone: z
      .object({ summary: z.string(), steps: z.array(titledTextSchema) })
      .nullable(),
    activity: z
      .object({ summary: z.string(), plans: z.array(titledTextSchema) })
      .nullable(),
    pastDogs: z.array(
      z.object({
        breedName: z.string(),
        sameBreed: z.boolean(),
        points: z.array(z.string())
      })
    )
  }),
  location: z
    .object({
      heading: z.string(),
      provinceName: z.string(),
      climateSummary: z.string(),
      climate: z.array(titledTextSchema),
      rules: z.array(titledTextSchema),
      budget: z.object({ text: z.string(), costGuidePath: z.string() })
    })
    .nullable(),
  screenings: z.object({
    heading: z.string(),
    intro: z.string(),
    parents: z.array(
      z.object({ breedName: z.string(), screenings: z.array(screeningSchema) })
    ),
    conditions: z.array(z.string())
  }),
  dnaPrimer: z.object({
    heading: z.string(),
    paragraphs: z.array(z.string()),
    pairings: z.array(z.object({ parents: z.string(), puppies: z.string() }))
  }),
  verification: z.object({
    heading: z.string(),
    steps: z.array(z.string()),
    notes: z.array(z.string())
  }),
  questionScript: z.object({
    heading: z.string(),
    intro: z.string(),
    groups: z.array(
      z.object({
        heading: z.string(),
        personalized: z.boolean(),
        questions: z.array(z.string())
      })
    )
  }),
  limits: z.object({ heading: z.string(), items: z.array(z.string()) }),
  bonus: z.array(bonusComparisonSchema).default([]),
  disclaimer: z.string()
});

const previewSchema = z.object({
  breed: breedSummarySchema,
  chapters: z.array(chapterSchema),
  introduction: introductionSchema,
  about: z.object({
    heading: z.string(),
    atAGlance: glanceSchema,
    temperament: z.array(z.string())
  }),
  sampleScreening: screeningSchema.nullable(),
  screeningNames: z.array(
    z.object({ breedName: z.string(), screenings: z.array(z.string()) })
  ),
  screeningCount: z.number()
});

const personalizationSchema = z.object({
  household: z.array(z.string()),
  homeType: z.string().nullable(),
  outdoorSpace: z.array(z.string()),
  otherPets: z.array(z.string()),
  dailyTime: z.string().nullable(),
  experience: z.string().nullable(),
  allergies: z.string().nullable(),
  province: z.string().nullable().default(null),
  hoursAlone: z.string().nullable().default(null),
  activities: z.array(z.string()).default([]),
  pastBreeds: z.array(z.string()).default([])
});

const handbookViewSchema = z.object({
  breed: z.object({ slug: z.string(), name: z.string() }),
  priceCents: z.number(),
  regularPriceCents: z.number(),
  currency: z.string(),
  owned: z.boolean(),
  purchasedAt: z.string().nullable(),
  preparedFor: z.string().nullable(),
  personalization: personalizationSchema,
  personalizationComplete: z.boolean(),
  homeProfileComplete: z.boolean().default(false),
  comparedWith: z.array(breedRefSchema).default([]),
  earlyReaderCopy: z.boolean().default(false),
  feedbackSubmitted: z.boolean().default(false),
  preview: previewSchema,
  handbook: handbookSchema.nullable()
});

const summarySchema = z.object({
  breedSlug: z.string(),
  breedName: z.string(),
  photoUrl: z.string().nullable()
});

const handbookListSchema = z.object({
  priceCents: z.number(),
  regularPriceCents: z.number(),
  currency: z.string(),
  owned: z.array(
    summarySchema.extend({
      purchasedAt: z.string().nullable(),
      matchesWaitlist: z.boolean().default(false)
    })
  ),
  suggested: z.array(
    summarySchema.extend({
      source: z
        .enum(['breederWaitlist', 'globalWaitlist'])
        .default('globalWaitlist')
    })
  ),
  earlyReader: summarySchema
    .extend({ remaining: z.number() })
    .nullable()
    .default(null)
});

const breedListSchema = z.object({
  breeds: z.array(
    z.object({
      slug: z.string(),
      name: z.string(),
      photoUrl: z.string().nullable().optional().default(null)
    })
  )
});

const checkoutSchema = z.object({
  paymentIntentId: z.string(),
  clientSecret: z.string(),
  amountCents: z.number().optional(),
  subtotalCents: z.number().optional(),
  province: z.string().nullable().optional()
});

const quoteSchema = z.object({
  subtotalCents: z.number(),
  totalCents: z.number(),
  currency: z.string(),
  province: z.string(),
  taxes: z.array(
    z.object({
      displayName: z.string(),
      percentage: z.number(),
      amountCents: z.number()
    })
  )
});

const fitSchema = z.object({
  breed: breedRefSchema,
  summary: z.string(),
  factors: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      fit: z.enum(['good', 'plan', 'consider']),
      headline: z.string()
    })
  )
});

const earlyReaderClaimSchema = z.object({ breedSlug: z.string() });

const feedbackSchema = z.object({ saved: z.boolean() });

const repriceSchema = z.object({
  amountCents: z.number(),
  currency: z.string()
});

const confirmSchema = z.object({
  owned: z.boolean(),
  breedSlug: z.string().nullable(),
  declined: z.boolean().default(false)
});

export type BreedHandbook = z.infer<typeof handbookSchema>;
export type HandbookPreview = z.infer<typeof previewSchema>;
export type HandbookScreening = z.infer<typeof screeningSchema>;
export type HandbookBonusComparison = z.infer<typeof bonusComparisonSchema>;
export type HandbookBonusSection = z.infer<typeof bonusSectionSchema>;
export type BreedHandbookView = Omit<
  z.infer<typeof handbookViewSchema>,
  'personalization'
> & { personalization: HandbookPersonalization };
export type BreedHandbookList = z.infer<typeof handbookListSchema>;
export type HandbookEarlyReaderOffer = NonNullable<
  BreedHandbookList['earlyReader']
>;
export type HandbookQuote = z.infer<typeof quoteSchema>;
export type HandbookFitResult = z.infer<typeof fitSchema>;
export type HandbookCheckoutSession = z.infer<typeof checkoutSchema>;

const handbookProductSchema = handbookViewSchema.pick({
  breed: true,
  priceCents: true,
  regularPriceCents: true,
  currency: true,
  preview: true
});

export type HandbookProduct = z.infer<typeof handbookProductSchema>;

export const parseHandbookProduct = (value: unknown): HandbookProduct =>
  handbookProductSchema.parse(value);

const readerFeedbackSchema = z.object({
  ratingCount: z.number(),
  averageRating: z.number().nullable(),
  quotes: z.array(
    z.object({
      firstName: z.string(),
      comment: z.string(),
      rating: z.number(),
      breedName: z.string(),
      earlyReader: z.boolean()
    })
  )
});

export type HandbookReaderFeedback = z.infer<typeof readerFeedbackSchema>;

export const parseHandbookReaderFeedback = (
  value: unknown
): HandbookReaderFeedback | null => {
  const parsed = readerFeedbackSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

const handbookApiPath = (breedSlug: string) =>
  `/me/handbooks/${encodeURIComponent(breedSlug)}`;

export const getMyHandbooks = () =>
  getProviderJson('/me/handbooks', { schema: handbookListSchema });

export const getHandbookBreeds = () =>
  getProviderJson('/me/handbooks/breeds', { schema: breedListSchema });

export const getHandbookPastBreedOptions = () =>
  getProviderJson('/me/handbooks/past-breed-options', {
    schema: breedListSchema
  });

export const getMyHandbook = async (
  breedSlug: string
): Promise<BreedHandbookView> =>
  (await getProviderJson(handbookApiPath(breedSlug), {
    schema: handbookViewSchema
  })) as BreedHandbookView;

type CheckoutDetails = {
  province?: HandbookProvinceCode | null;
  personalization?: HandbookPersonalization | null;
};

export const startHandbookCheckout = (
  breedSlug: string,
  details: CheckoutDetails = {}
) =>
  postProviderJson(`${handbookApiPath(breedSlug)}/checkout/start`, details, {
    schema: checkoutSchema
  });

const postPublicJson = async <TSchema extends z.ZodTypeAny>(
  path: string,
  body: unknown,
  schema: TSchema,
  fallbackMessage: string
): Promise<z.infer<TSchema>> => {
  const response = await fetch(`/api/v1.0/public${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      accept: 'application/json'
    },
    body: JSON.stringify(body)
  });

  const payload = (await response.json().catch(() => null)) as {
    data?: unknown;
    error?: string;
    code?: string;
  } | null;

  if (!response.ok) {
    throw new ApiError(payload?.error ?? fallbackMessage, {
      status: response.status,
      body: payload?.code ? JSON.stringify({ code: payload.code }) : undefined
    });
  }

  return schema.parse(payload?.data);
};

export const HANDBOOK_EMAIL_VERIFICATION_REQUIRED =
  'EMAIL_VERIFICATION_REQUIRED';

export const handbookErrorCode = (error: unknown): string | null => {
  if (!(error instanceof ApiError) || !error.body) return null;
  try {
    const parsed = JSON.parse(error.body) as { code?: unknown };
    return typeof parsed.code === 'string' ? parsed.code : null;
  } catch {
    return null;
  }
};

export const quoteHandbookCheckout = (input: {
  priceCents: number;
  province: HandbookProvinceCode;
}) =>
  postPublicJson(
    '/handbooks/quote',
    input,
    quoteSchema,
    'We could not work out the tax'
  );

export const checkHandbookFit = (
  breedSlug: string,
  personalization: HandbookPersonalization
) =>
  postPublicJson(
    `/handbooks/${encodeURIComponent(breedSlug)}/fit`,
    { personalization },
    fitSchema,
    'We could not check your answers'
  );

export const claimEarlyReaderCopy = (breedSlug: string) =>
  postProviderJson(`${handbookApiPath(breedSlug)}/early-reader`, undefined, {
    schema: earlyReaderClaimSchema
  });

export const saveHandbookFeedback = (
  breedSlug: string,
  feedback: { rating: number; comment: string | null; mayQuote: boolean }
) =>
  putProviderJson(`${handbookApiPath(breedSlug)}/feedback`, feedback, {
    schema: feedbackSchema
  });

export const startGuestHandbookCheckout = (
  breedSlug: string,
  account: CheckoutDetails & {
    email: string;
    firstName: string;
    verificationToken: string;
    marketingAttribution?: Record<string, unknown> | null;
  }
): Promise<HandbookCheckoutSession> =>
  postPublicJson(
    `/handbooks/${encodeURIComponent(breedSlug)}/checkout`,
    account,
    checkoutSchema,
    'We could not open checkout'
  );

export const repriceHandbookCheckout = (
  paymentIntentId: string,
  province: HandbookProvinceCode
) =>
  postProviderJson(
    `/me/handbooks/checkout/${encodeURIComponent(paymentIntentId)}/reprice`,
    { province },
    { schema: repriceSchema }
  );

export const saveHandbookPersonalization = async (
  breedSlug: string,
  personalization: HandbookPersonalization
): Promise<BreedHandbookView> =>
  (await putProviderJson(
    `${handbookApiPath(breedSlug)}/personalization`,
    { personalization },
    { schema: handbookViewSchema }
  )) as BreedHandbookView;

export const confirmHandbookCheckout = (paymentIntentId: string) =>
  postProviderJson(
    `/me/handbooks/checkout/${encodeURIComponent(paymentIntentId)}/confirm`,
    undefined,
    { schema: confirmSchema }
  );
